import { theme } from 'antd';
import { useManagePermittedRoles } from 'library/hooks';
import { buildDeviceOptionsWithIntegration } from 'library/helpers/integrationHelpers';
import SuperFetch from 'library/helpers/superFetch';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPermissionManageableDevices } from 'reduxStore/devices/selectors';
import { Notification } from 'zComponents/atoms';
import { Button, Flex, Form, Modal, Select, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ACTION_UNLOCK = 1;
const RemoteUnlockModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { colorTextLabel, paddingXL, borderRadiusLG, paddingSM }
	} = theme.useToken();

	// ✅ Redux State
	const devices = useSelector(selectPermissionManageableDevices);

	// ✅ Local State
	const [selectedDevice, setSelectedDevice] = useState(null);
	const [internalVisible, setInternalVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	// ✅ Permissions
	const canRemoteUnlock = useManagePermittedRoles('aCRemoteUnlock');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const visible = visibleProp || internalVisible;
	const setVisible = setVisibleProp || setInternalVisible;

	// ✅ Handlers
	const handleCancel = () => {
		setSelectedDevice(null);
		setVisible(false);
	};

	const handleUnlock = async (deviceID) => {
		try {
			setLoading(true);
			const res = await SuperFetch.post(
				`/devices/action`,
				{ deviceIDs: [deviceID], actionID: ACTION_UNLOCK },
				{}
			);

			if (res.statusCode === 200) {
				Notification('success', res.info);
				handleCancel();
			} else {
				Notification('error', res.data?.[0]?.errmsg || 'An error occurred');
			}
		} catch (error) {
			Notification('error', error.message || 'The lock cannot be unlocked');
		} finally {
			setLoading(false);
		}
	};

	// ✅ Options
	const devicesOptions = buildDeviceOptionsWithIntegration(devices);

	return (
		<>
			<Tooltip title={!canRemoteUnlock ? permissionDeniedMessage : 'Remote Unlock'} placement='top'>
				<span onClick={(e) => canRemoteUnlock && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canRemoteUnlock}>
							Remote Unlock
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				centered
				footer={[
					<Flex gap={paddingSM} align='center' justify='flex-end'>
						<Button onClick={handleCancel} key='cancel'>
							Cancel
						</Button>
						<Button
							key='unlock'
							type='primary'
							disabled={!selectedDevice}
							loading={loading}
							onClick={() => handleUnlock(selectedDevice)}
						>
							Unlock
						</Button>
					</Flex>
				]}
				closable={false}
				styles={{ content: { padding: paddingXL, borderRadius: borderRadiusLG } }}
			>
				<Title level={4} style={{ margin: 0 }}>
					Remote Unlock
				</Title>
				<Text
					style={{
						color: colorTextLabel,
						lineHeight: '20px'
					}}
				>
					Remotely unlock individual devices or device groups to assist people experiencing issues.
				</Text>

				<Form layout='vertical' style={{ marginTop: 22 }}>
					<Form.Item label={<strong>Device</strong>}>
						<Select
							showSearch
							placeholder='Device Name'
							value={selectedDevice}
							onChange={setSelectedDevice}
							options={devicesOptions}
							filterOption={(input, option) =>
							(option?.searchLabel || option?.displayLabel || '')
								.toLowerCase()
								.includes(input.toLowerCase())
						}
							style={{ marginBottom: paddingXL }}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default RemoteUnlockModal;
